import { Skeleton } from '@vizit/atoms';
import { ScoreColorVariant, ScoreDescriptions } from '@vizit/constants';
import { cva, cx } from '@vizit/theme';
import { getScoreColorVariant, getScoreDescription } from '@vizit/utils';
import type * as React from 'react';
import { Text, TextSize } from '../text';

interface ScorePillProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The ref to use for the score pill.
   *
   * @optional
   * @type React.Ref<HTMLDivElement>
   * @default undefined
   */
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Score value to calculate display text and color.
   *
   * @optional
   * @type number | null
   * @default null
   */
  score: number | null;
  /**
   * Whether to omit the score and display UNSCORED.
   *
   * @optional
   * @type boolean
   * @default false
   */
  omitScore?: boolean;
  /**
   * Whether is loading.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
}

const wrapperClasses = cva({
  base: 'inline-flex items-center justify-center p-px min-w-24',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'bg-gray-utility-300',
      [ScoreColorVariant.VeryLow]: 'bg-error-600',
      [ScoreColorVariant.Low]: 'bg-error-600',
      [ScoreColorVariant.Moderate]: 'bg-warning-600',
      [ScoreColorVariant.High]: 'bg-green-yellow-600',
      [ScoreColorVariant.VeryHigh]: 'bg-success-600',
    },
  },
  defaultVariants: {
    colorVariant: ScoreColorVariant.NotApplicable,
  },
});

const innerClasses = cva({
  base: 'inline-flex items-center justify-center px-4 py-1 w-full',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'bg-secondary',
      [ScoreColorVariant.VeryLow]: 'bg-error-50',
      [ScoreColorVariant.Low]: 'bg-error-50',
      [ScoreColorVariant.Moderate]: 'bg-warning-50',
      [ScoreColorVariant.High]: 'bg-green-yellow-50',
      [ScoreColorVariant.VeryHigh]: 'bg-success-50',
    },
  },
  defaultVariants: {
    colorVariant: ScoreColorVariant.NotApplicable,
  },
});

const textClasses = cva({
  base: '',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'text-secondary',
      [ScoreColorVariant.VeryLow]: 'text-error-700',
      [ScoreColorVariant.Low]: 'text-error-700',
      [ScoreColorVariant.Moderate]: 'text-warning-700',
      [ScoreColorVariant.High]: 'text-green-yellow-700',
      [ScoreColorVariant.VeryHigh]: 'text-success-700',
    },
  },
  defaultVariants: {
    colorVariant: ScoreColorVariant.NotApplicable,
  },
});

/**
 * A badge to display a status or label.
 *
 * @param {ScorePillProps} props - The props to use for the pill.
 */
function ScorePill(props: ScorePillProps) {
  const { ref, children, className, score, omitScore = false, isLoading = false, ...other } = props;

  const formattedScore = score === null ? ScoreDescriptions.NotApplicable : getScoreDescription(score);
  const colorVariant = omitScore ? ScoreColorVariant.NotApplicable : getScoreColorVariant(score);

  return (
    <>
      {isLoading && <Skeleton className="px-4 py-1 h-12 w-24" variant="square" />}
      {!isLoading && (
        <div
          ref={ref}
          className={wrapperClasses({
            className,
            colorVariant,
          })}
          {...other}
        >
          <div className={cx(innerClasses({ colorVariant }), textClasses({ colorVariant }))}>
            <Text elementType="span" size={TextSize.Xs} showEllipsis={false} className="text-inherit">
              {omitScore ? ScoreDescriptions.Unscored : formattedScore}
            </Text>
          </div>
        </div>
      )}
    </>
  );
}

export type { ScorePillProps };
export { ScorePill };
